/**
 * Profile_Context — facts the AI_Companion already knows about the End_User.
 *
 * Injected into every System_Prompt so conversations feel familiar from
 * message zero. Defaults below seed the spec values from Requirement 1.2
 * and back the Requirement 1.5 fallback when no profile is persisted.
 */

export interface ProfileContext {
  schemaVersion: 1;
  /** Display name. Seeded empty so the End_User can choose later. */
  displayName: string;
  /** ISO `YYYY-MM-DD`. Default `'2002-04-13'`. */
  dateOfBirth: string;
  /** Default `'Türkiye'`. */
  countryOfOrigin: string;
  /** Default `'Greece'`. */
  countryOfResidence: string;
  /** Default `['Greek', 'English']`. */
  languagesLearning: string[];
  /** Default `'curly hair, looks lovely with bangs'`. */
  hairDescription: string;
  /** Default `'pasta'`. */
  favoriteFood: string;
  /** Default `{ species: 'cat', name: 'Sütlaç' }`. */
  pet: { species: string; name: string };
  /** Default `'charm bracelet'`. */
  accessory: string;
  /** Default `'Harry Potter'`. */
  fandom: string;
  /**
   * Default `['collecting cute figurines', 'decorative toys']`. Note
   * that build-it kits like Lego are explicitly NOT her thing — that
   * nuance is encoded in `notes` so the prompt surfaces it without
   * cluttering the comma-separated hobbies field.
   */
  hobbies: string[];
  /** Default `['the sea', 'starfish']`. */
  interests: string[];
  /** Default `'pink'`. */
  favoriteColor: string;
  /** Free-form notes. Default empty string. */
  notes: string;
}

/**
 * Returns the seeded default Profile_Context (Requirements 1.2, 1.5).
 *
 * The function returns a fresh object on each call so callers can mutate
 * the result without leaking back into shared module state.
 */
export function seedDefaultProfile(): ProfileContext {
  return {
    schemaVersion: 1,
    displayName: 'Meryem Özdemiroğlu',
    dateOfBirth: '2002-04-13',
    countryOfOrigin: 'Türkiye',
    countryOfResidence: 'Greece',
    languagesLearning: ['Greek', 'English'],
    hairDescription: 'curly hair, looks lovely with bangs',
    favoriteFood: 'pasta',
    pet: { species: 'cat', name: 'Sütlaç' },
    accessory: 'charm bracelet',
    fandom: 'Harry Potter',
    hobbies: ['collecting cute figurines', 'decorative toys'],
    interests: ['the sea', 'starfish'],
    favoriteColor: 'pink',
    notes:
      'She loves the finished, displayed look of cute figurines and decorative toys, but she does not enjoy assembling them — building Lego or similar build-it kits is NOT her hobby. Do not assume she likes putting things together; treat her hobby as collecting and displaying the finished pieces. She likes being called "fıstığım", but use it rarely: at most once or twice across roughly every 5-10 assistant replies, never back-to-back, and only when it fits naturally.',
  };
}
